import { StyleSheet } from 'react-native'
import React, { useEffect, useId } from 'react'
import Animated, {
  interpolate,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import InputLabelInline from './InputLabelInline'
import {
  useAnimatedInputBackground,
  useAnimatedInputBorder,
} from './useAnimatedInputBorder'
import { SuccessIndicator, ErrorIndicator } from './InputIndicator'
import { InputContainerProps } from './types'

// const AnimatedLinearGradient = Animated.createAnimatedComponent(LinearGradient)

const InputContainer = ({
  className,
  children,
  onLabelPress,
  style,
  label,
  labelWidth,
  labelVariant = 'inline',
  icon,
  required,
  errorMessage,
  successMessage,
  hideIndicator,
  focus,
  success,
  error,
  center,
  disabled,
  readonly,
}: InputContainerProps) => {
  const id = useId()

  const styleBorder = useAnimatedInputBorder({
    focus,
    error,
    success,
    disabled,
    readonly,
  })

  const styleBackground = useAnimatedInputBackground({
    focus,
    error,
    success,
    disabled,
    readonly,
  })

  // const gradientOpacity = useAnimatedStyle(() => {
  //   return {
  //     opacity: interpolate(disabled?.value || 0, [0, 1], [1, 0]),
  //   }
  // })

  const stylePointerEvents = useAnimatedStyle(() => ({
    pointerEvents: disabled?.value || readonly?.value ? 'none' : 'auto',
  }))

  const containerError = useAnimatedStyle(() => {
    const val = hideIndicator ? 0 : success.value || error.value
    return {
      paddingRight: hideIndicator ? 0 : interpolate(val, [0, 1], [0, 40]),
    }
  })

  const styleErrorIndicator = useAnimatedStyle(() => {
    const val = hideIndicator ? 0 : error.value

    const translateX = interpolate(
      errorMessage ? val || 0 : 0,
      [0, 0.1, 0.3, 0.5, 0.6, 1],
      [0, -2, 2, -2, 0, 0]
    )

    return {
      transform: [
        {
          translateX,
        },
      ],
      opacity: val,
    }
  })

  const styleSuccessIndicator = useAnimatedStyle(() => {
    const val = hideIndicator || !successMessage ? 0 : success.value

    return {
      opacity: val,
    }
  })

  useEffect(() => {
    success.set(withSpring(successMessage ? 1 : 0))
    error.set(withSpring(errorMessage ? 1 : 0))
  }, [successMessage, errorMessage, success, error])

  return (
    <Animated.View
      style={[style, stylePointerEvents]}
      className={cn(
        'relative flex h-12 w-full flex-row overflow-hidden rounded-lg py-0 pr-2',
        center && 'px-0',
        className
      )}
    >
      <Animated.View
        style={[StyleSheet.absoluteFill, styleBackground, styleBorder]}
        className="overflow-hidden rounded-lg"
      >
        {/* <Animated.View className="flex-1" style={gradientOpacity}>
          <AnimatedLinearGradient
            style={[{ flex: 1 }]}
            colors={[colors.white75, colors.white10]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
          />
        </Animated.View> */}
      </Animated.View>

      {labelVariant === 'inline' && (
        <InputLabelInline
          onPress={onLabelPress}
          label={label}
          labelWidth={labelWidth}
          icon={icon}
          focus={focus}
          success={success}
          successMessage={successMessage}
          error={error}
          errorMessage={errorMessage}
          disabled={disabled}
          readonly={readonly}
          required={required}
        />
      )}
      <Animated.View
        className={cn('relative flex-1 flex-row')}
        style={containerError}
      >
        {children}
        {!hideIndicator && (
          <>
            <Animated.View
              style={styleSuccessIndicator}
              className={cn(
                'absolute right-0 top-0 flex h-full items-center justify-center',
                !successMessage && 'pointer-events-none'
              )}
            >
              <SuccessIndicator id={id} message={successMessage} />
            </Animated.View>
            <Animated.View
              style={styleErrorIndicator}
              className={cn(
                'absolute right-0 top-0 flex h-full items-center justify-center',
                !errorMessage && 'pointer-events-none'
              )}
            >
              <ErrorIndicator id={id} message={errorMessage} />
            </Animated.View>
          </>
        )}
      </Animated.View>
    </Animated.View>
  )
}

InputContainer.displayName = 'InputContainer'

export default InputContainer
